import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Settings, 
  Database, 
  Shield, 
  Bell, 
  Save, 
  RefreshCw,
  CheckCircle,
  User,
  FileText,
  Plus,
  Edit,
  Trash2,
  Copy,
  ExternalLink,
  TestTube,
  Key
} from 'lucide-react';
import { toast } from 'react-toastify';
import { BACKEND_CONFIG } from '../config/backend';

const SettingsPage = ({ user }) => {
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Estados para as configurações
  const [generalSettings, setGeneralSettings] = useState({
    systemName: 'Sistema de Consultas Bitrix',
    systemDescription: 'Painel de consultas e gerenciamento de APIs',
    timezone: 'America/Sao_Paulo',
    language: 'pt-BR',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '24h'
  });

  const [apiSettings, setApiSettings] = useState({
    timeout: 30000,
    retryAttempts: 3,
    retryDelay: 1000,
    enableLogging: true,
    enableMetrics: true
  });

  const [securitySettings, setSecuritySettings] = useState({
    sessionTimeout: 3600,
    maxLoginAttempts: 5,
    passwordMinLength: 8,
    requireSpecialChars: true,
    enable2FA: false,
    enableAuditLog: true
  });

  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    apiStatusAlerts: true,
    errorAlerts: true,
    successAlerts: false
  });

  // Estado para Cookies do Bitrix Antigo
  const [bitrixCookiesOld, setBitrixCookiesOld] = useState({
    cookieString: '',
    timestamp: null,
    expiresAt: null,
    status: 'inactive',
    cookiesList: [] // Lista de cookies parseados
  });

  // Estado para Cookies do Bitrix Novo
  const [bitrixCookiesNew, setBitrixCookiesNew] = useState({
    cookieString: '',
    timestamp: null,
    expiresAt: null,
    status: 'inactive',
    cookiesList: [] // Lista de cookies parseados
  });

  // Carregar configurações salvas
  useEffect(() => {
    loadSettings();
    loadBitrixCookiesStatus('old');
    loadBitrixCookiesStatus('new');
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      // Aqui você pode carregar as configurações do localStorage ou backend
      const savedSettings = localStorage.getItem('systemSettings');
      if (savedSettings) {
        const parsed = JSON.parse(savedSettings);
        setGeneralSettings(parsed.general || generalSettings);
        setApiSettings(parsed.api || apiSettings);
        setSecuritySettings(parsed.security || securitySettings);
        setNotificationSettings(parsed.notifications || notificationSettings);
      }
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const loadBitrixCookiesStatus = async (instance = 'old') => {
    try {
      const url = `${BACKEND_CONFIG.BASE_URL}/api/settings/bitrix-cookies?instance=${instance}`;
      console.log(`🔍 Carregando status dos cookies do Bitrix ${instance === 'old' ? 'Antigo' : 'Novo'}:`, url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      console.log('📡 Resposta do status dos cookies:', response.status, response.statusText);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Dados recebidos:', data);
        
        if (data.success && data.data) {
          // Parsear cookies se existirem
          let cookiesList = [];
          if (data.data.cookiesList && data.data.cookiesList.length > 0) {
            cookiesList = data.data.cookiesList;
          } else if (data.data.cookieString) {
            // Fallback: parsear manualmente se não vier parseado
            const cookiePairs = data.data.cookieString.split(';').map(c => c.trim()).filter(c => c);
            cookiesList = cookiePairs.map(cookiePair => {
              const [name, ...valueParts] = cookiePair.split('=');
              const value = valueParts.join('=');
              return {
                name: name.trim(),
                value: value.trim(),
                full: cookiePair.trim()
              };
            });
          }

          // Substituir status/lista pelo que veio do backend (não misturar com antigos)
          const stateUpdate = {
            status: data.data.status || 'inactive',
            timestamp: data.data.timestamp || null,
            expiresAt: data.data.expiresAt || null,
            cookiesList: cookiesList
          };
          
          if (instance === 'old') {
            setBitrixCookiesOld(prev => ({ ...prev, ...stateUpdate }));
          } else {
            setBitrixCookiesNew(prev => ({ ...prev, ...stateUpdate }));
          }
        }
      } else {
        // Tentar ler a mensagem de erro
        try {
          const errorData = await response.json();
          console.error('❌ Erro ao carregar status:', errorData);
        } catch (e) {
          console.error('❌ Erro HTTP:', response.status, response.statusText);
        }
      }
    } catch (error) {
      console.error('❌ Erro ao carregar status dos cookies:', error);
      // Não mostrar erro ao usuário, apenas log
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      
      // Salvar no localStorage (ou enviar para o backend)
      const allSettings = {
        general: generalSettings,
        api: apiSettings,
        security: securitySettings,
        notifications: notificationSettings
      };
      
      localStorage.setItem('systemSettings', JSON.stringify(allSettings));
      
      // Simular salvamento no backend
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      toast.error('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const resetSettings = () => {
    if (window.confirm('Tem certeza que deseja redefinir todas as configurações?')) {
      loadSettings();
      toast.info('Configurações redefinidas para os valores padrão');
    }
  };

  const tabs = [
    { id: 'general', label: 'Geral', icon: Settings },
    { id: 'api', label: 'APIs', icon: Database },
    { id: 'security', label: 'Segurança', icon: Shield },
    { id: 'notifications', label: 'Notificações', icon: Bell },
    { id: 'bitrix-cookies', label: 'Bitrix Cookies', icon: Key }
  ];

  const renderGeneralTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nome do Sistema
          </label>
          <input
            type="text"
            value={generalSettings.systemName}
            onChange={(e) => setGeneralSettings(prev => ({ ...prev, systemName: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Descrição
          </label>
          <input
            type="text"
            value={generalSettings.systemDescription}
            onChange={(e) => setGeneralSettings(prev => ({ ...prev, systemDescription: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Fuso Horário
          </label>
          <select
            value={generalSettings.timezone}
            onChange={(e) => setGeneralSettings(prev => ({ ...prev, timezone: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="America/Sao_Paulo">São Paulo (GMT-3)</option>
            <option value="America/Manaus">Manaus (GMT-4)</option>
            <option value="America/Belem">Belém (GMT-3)</option>
            <option value="UTC">UTC</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Idioma
          </label>
          <select
            value={generalSettings.language}
            onChange={(e) => setGeneralSettings(prev => ({ ...prev, language: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="pt-BR">Português (Brasil)</option>
            <option value="en-US">English (US)</option>
            <option value="es-ES">Español</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Formato de Data
          </label>
          <select
            value={generalSettings.dateFormat}
            onChange={(e) => setGeneralSettings(prev => ({ ...prev, dateFormat: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Formato de Hora
          </label>
          <select
            value={generalSettings.timeFormat}
            onChange={(e) => setGeneralSettings(prev => ({ ...prev, timeFormat: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="24h">24 horas</option>
            <option value="12h">12 horas</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderApiTab = () => (
    <div className="space-y-6">
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Configurações Gerais</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Timeout (ms)
            </label>
            <input
              type="number"
              value={apiSettings.timeout}
              onChange={(e) => setApiSettings(prev => ({ ...prev, timeout: parseInt(e.target.value) }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tentativas de Retry
            </label>
            <input
              type="number"
              value={apiSettings.retryAttempts}
              onChange={(e) => setApiSettings(prev => ({ ...prev, retryAttempts: parseInt(e.target.value) }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Delay entre Tentativas (ms)
            </label>
            <input
              type="number"
              value={apiSettings.retryDelay}
              onChange={(e) => setApiSettings(prev => ({ ...prev, retryDelay: parseInt(e.target.value) }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
        
        <div className="mt-4 space-y-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={apiSettings.enableLogging}
              onChange={(e) => setApiSettings(prev => ({ ...prev, enableLogging: e.target.checked }))}
              className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Habilitar logs de API</span>
          </label>
          
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={apiSettings.enableMetrics}
              onChange={(e) => setApiSettings(prev => ({ ...prev, enableMetrics: e.target.checked }))}
              className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Habilitar métricas de performance</span>
          </label>
        </div>
      </div>
    </div>
  );

  const renderSecurityTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Timeout da Sessão (segundos)
          </label>
          <input
            type="number"
            value={securitySettings.sessionTimeout}
            onChange={(e) => setSecuritySettings(prev => ({ ...prev, sessionTimeout: parseInt(e.target.value) }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Máximo de Tentativas de Login
          </label>
          <input
            type="number"
            value={securitySettings.maxLoginAttempts}
            onChange={(e) => setSecuritySettings(prev => ({ ...prev, maxLoginAttempts: parseInt(e.target.value) }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tamanho Mínimo da Senha
          </label>
          <input
            type="number"
            value={securitySettings.passwordMinLength}
            onChange={(e) => setSecuritySettings(prev => ({ ...prev, passwordMinLength: parseInt(e.target.value) }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>
      
      <div className="space-y-3">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={securitySettings.requireSpecialChars}
            onChange={(e) => setSecuritySettings(prev => ({ ...prev, requireSpecialChars: e.target.checked }))}
            className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">Requer caracteres especiais na senha</span>
        </label>
        
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={securitySettings.enable2FA}
            onChange={(e) => setSecuritySettings(prev => ({ ...prev, enable2FA: e.target.checked }))}
            className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">Habilitar autenticação de dois fatores (2FA)</span>
        </label>
        
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={securitySettings.enableAuditLog}
            onChange={(e) => setSecuritySettings(prev => ({ ...prev, enableAuditLog: e.target.checked }))}
            className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">Habilitar log de auditoria</span>
        </label>
      </div>
    </div>
  );

  const renderNotificationsTab = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Preferências de Notificação</h3>
        
        <div className="space-y-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={notificationSettings.emailNotifications}
              onChange={(e) => setNotificationSettings(prev => ({ ...prev, emailNotifications: e.target.checked }))}
              className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Notificações por email</span>
          </label>
          
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={notificationSettings.pushNotifications}
              onChange={(e) => setNotificationSettings(prev => ({ ...prev, pushNotifications: e.target.checked }))}
              className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Notificações push no navegador</span>
          </label>
          
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={notificationSettings.apiStatusAlerts}
              onChange={(e) => setNotificationSettings(prev => ({ ...prev, apiStatusAlerts: e.target.checked }))}
              className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Alertas de status das APIs</span>
          </label>
          
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={notificationSettings.errorAlerts}
              onChange={(e) => setNotificationSettings(prev => ({ ...prev, errorAlerts: e.target.checked }))}
              className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Alertas de erro</span>
          </label>
          
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={notificationSettings.successAlerts}
              onChange={(e) => setNotificationSettings(prev => ({ ...prev, successAlerts: e.target.checked }))}
              className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Alertas de sucesso</span>
          </label>
        </div>
      </div>
    </div>
  );

  const renderBitrixCookiesTab = () => (
    <div className="space-y-8">
      {/* Seção Bitrix Antigo */}
      {renderCookiesSectionForInstance('old', bitrixCookiesOld, setBitrixCookiesOld)}
      
      {/* Separador */}
      <div className="border-t border-gray-300 my-8"></div>
      
      {/* Seção Bitrix Novo */}
      {renderCookiesSectionForInstance('new', bitrixCookiesNew, setBitrixCookiesNew)}
    </div>
  );

  // Função auxiliar para renderizar seção de cookies de uma instância
  const renderCookiesSectionForInstance = (instance, cookiesState, setCookiesState) => {
    const instanceName = instance === 'old' ? 'Antigo' : 'Novo';
    const instanceUrl = instance === 'old' ? 'villela.bitrix24.com.br' : 'grupovillela.bitrix24.com.br';
    const isOld = instance === 'old';
    
    return (
      <div className="space-y-6">
        {/* Título da Seção */}
        <div className={`${isOld ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'} rounded-lg p-4 border`}>
          <h3 className={`text-lg font-bold ${isOld ? 'text-blue-900' : 'text-green-900'} mb-2`}>
            🍪 Cookies do Bitrix {instanceName}
          </h3>
          <p className={`text-sm ${isOld ? 'text-blue-700' : 'text-green-700'}`}>
            Configure os cookies de sessão para o Bitrix {instanceName} ({instanceUrl})
          </p>
        </div>

        {/* Informações */}
        <div className={`${isOld ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'} rounded-lg p-4 border`}>
          <div className="flex gap-3">
            <Key className={`w-5 h-5 ${isOld ? 'text-blue-600' : 'text-green-600'} mt-0.5 flex-shrink-0`} />
            <div className={`text-sm ${isOld ? 'text-blue-700' : 'text-green-700'}`}>
              <p className="font-medium mb-2">📍 Como coletar cookies do Bitrix {instanceName}</p>
              <ol className="space-y-2 list-decimal list-inside">
                <li>Faça login no Bitrix {instanceName} no navegador</li>
                <li>Pressione <span className="font-mono bg-gray-200 px-2 py-1 rounded">F12</span> para abrir DevTools</li>
                <li>Vá em <span className="font-mono">Application → Cookies → {instanceUrl}</span></li>
              <li>
                <strong>Método 1 (Recomendado):</strong> Clique com botão direito na tabela de cookies → 
                <span className="font-mono bg-gray-200 px-1 rounded">Copy all as cURL</span> ou use uma extensão de navegador
              </li>
              <li>
                <strong>Método 2 (Manual):</strong> Para cada cookie, copie no formato: 
                <span className="font-mono bg-gray-200 px-1 rounded">nome=valor</span> e separe por 
                <span className="font-mono bg-gray-200 px-1 rounded">; </span> (ponto e vírgula + espaço)
              </li>
              <li>Cole a string completa abaixo no formato: <span className="font-mono bg-gray-200 px-1 rounded">BITRIX_SM_CC=Y; PHPSESSID=abc123; ...</span></li>
            </ol>
            </div>
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded text-xs">
              <strong>⚠️ Formato Correto:</strong>
              <div className="mt-1 font-mono text-xs bg-white p-2 rounded border">
                BITRIX_SM_CC=Y; PHPSESSID=6Hq6XnvmmMNKiWIuALEKQJ16C8TluI5z; BITRIX_SM_UIDH=GU3Z4ogxKZ164HRKha63vibbrF7Je4A9; ...
              </div>
              <div className="mt-2 text-red-600">
                <strong>❌ NÃO copie a tabela inteira!</strong> Copie apenas os valores no formato nome=valor separados por ponto e vírgula.
              </div>
            </div>
          </div>
        </div>

        {/* Status dos Cookies */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Status dos Cookies</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className={`${isOld ? 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200' : 'bg-gradient-to-br from-green-50 to-green-100 border-green-200'} rounded-lg p-4 border`}>
              <p className={`text-sm ${isOld ? 'text-blue-600' : 'text-green-600'} font-medium`}>Status</p>
              <p className={`text-2xl font-bold ${isOld ? 'text-blue-900' : 'text-green-900'} mt-1`}>
                {cookiesState.status === 'active' ? '✅ Ativo' : '❌ Inativo'}
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
              <p className="text-sm text-purple-600 font-medium">Última Atualização</p>
              <p className="text-lg font-bold text-purple-900 mt-1">
                {cookiesState.timestamp ? new Date(cookiesState.timestamp).toLocaleDateString('pt-BR') : 'Nunca'}
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">
              <p className="text-sm text-orange-600 font-medium">Validade</p>
              <p className="text-lg font-bold text-orange-900 mt-1">
                {cookiesState.expiresAt ? new Date(cookiesState.expiresAt).toLocaleDateString('pt-BR') : 'Indefinido'}
              </p>
            </div>
          </div>

          {/* Lista de Cookies Salvos */}
          {cookiesState.cookiesList && cookiesState.cookiesList.length > 0 && (
            <div className="mt-6">
              <h4 className="text-md font-medium text-gray-700 mb-3">
                🍪 Cookies Salvos ({cookiesState.cookiesList.length})
              </h4>
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 max-h-96 overflow-y-auto">
                <div className="space-y-2">
                  {cookiesState.cookiesList.map((cookie, index) => (
                    <div 
                    key={index}
                    className="bg-white rounded-lg border border-gray-200 p-3 hover:border-blue-300 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-sm font-semibold text-blue-700">
                            {cookie.name}
                          </span>
                          <span className="text-xs text-gray-500">=</span>
                        </div>
                        <div className="font-mono text-xs text-gray-600 break-all">
                          {cookie.value.length > 100 
                            ? (
                                <details className="cursor-pointer">
                                  <summary className="text-blue-600 hover:text-blue-800">
                                    {cookie.value.substring(0, 100)}...
                                  </summary>
                                  <div className="mt-2 p-2 bg-gray-50 rounded border">
                                    {cookie.value}
                                  </div>
                                </details>
                              )
                            : cookie.value
                          }
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(cookie.full);
                          toast.success(`Cookie ${cookie.name} copiado!`);
                        }}
                        className="flex-shrink-0 p-1 text-gray-400 hover:text-blue-600 transition-colors"
                        title="Copiar cookie"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                    ))}
                </div>
              </div>
            </div>
            )}
          </div>

        {/* Inserção de Cookies */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Inserir Cookies Bitrix {instanceName}</h3>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              String de Cookies
            </label>
            <textarea
              value={cookiesState.cookieString}
              onChange={(e) => {
                let value = e.target.value;
              
              // Tentar converter se o usuário colou uma tabela
              // Verifica se parece ser uma tabela (múltiplas linhas com tabs ou espaços)
              if (value.includes('\t') && value.split('\n').length > 3) {
                try {
                  // Tentar converter tabela para formato de cookies
                  const lines = value.split('\n').filter(line => line.trim());
                  const cookies = [];
                  
                  for (const line of lines) {
                    const parts = line.split('\t').filter(p => p.trim());
                    if (parts.length >= 2) {
                      const name = parts[0].trim();
                      const cookieValue = parts[1].trim();
                      if (name && cookieValue && name !== 'Name' && cookieValue !== 'Value') {
                        cookies.push(`${name}=${cookieValue}`);
                      }
                    }
                  }
                  
                  if (cookies.length > 0) {
                    value = cookies.join('; ');
                    toast.info(`✅ Convertidos ${cookies.length} cookies da tabela para formato correto!`);
                  }
                } catch (err) {
                console.error('Erro ao converter tabela:', err);
                }
              }
              
                setCookiesState(prev => ({ ...prev, cookieString: value }));
              }}
              placeholder={`Cole aqui a string de cookies do Bitrix ${instanceName} no formato: BITRIX_SM_CC=Y; PHPSESSID=abc123; ...`}
              className={`w-full h-32 px-3 py-2 border rounded-lg ${isOld ? 'focus:ring-blue-500' : 'focus:ring-green-500'} focus:ring-2 focus:border-transparent font-mono text-sm ${
                cookiesState.cookieString.trim().length > 0 && 
                (!cookiesState.cookieString.includes('=') || cookiesState.cookieString.length < 20)
                  ? 'border-red-300 bg-red-50' 
                  : 'border-gray-300'
              }`}
            />
            <div className="mt-2 flex items-start gap-2">
              <p className="text-xs text-gray-500 flex-1">
                ℹ️ <strong>Dica:</strong> Se você colar a tabela inteira do DevTools, o sistema tentará converter automaticamente. 
                Ou copie manualmente no formato: <span className="font-mono">nome=valor; nome2=valor2</span>
              </p>
              {cookiesState.cookieString.trim().length > 0 && (
                <div className="text-xs">
                  {cookiesState.cookieString.includes('=') && cookiesState.cookieString.length >= 20 ? (
                    <span className="text-green-600 font-medium">✅ Formato válido</span>
                  ) : (
                    <span className="text-red-600 font-medium">
                      ⚠️ {!cookiesState.cookieString.includes('=') 
                        ? 'Falta o caractere "="' 
                        : cookiesState.cookieString.length < 20 
                          ? `Muito curto (${cookiesState.cookieString.length}/20 caracteres)`
                          : 'Formato inválido'}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 flex gap-3">
            <button
              onClick={() => {
                if (cookiesState.cookieString) {
                  navigator.clipboard.writeText(cookiesState.cookieString);
                  toast.success('Cookies copiados para a área de transferência!');
                }
              }}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <Copy className="w-4 h-4" />
              Copiar
            </button>

            <button
              onClick={() => setCookiesState(prev => ({ ...prev, cookieString: '' }))}
              className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Limpar
            </button>

            <button
              onClick={async () => {
                const cookieString = cookiesState.cookieString.trim();
              
                // Validação no frontend antes de enviar
                if (!cookieString) {
                  toast.error('Por favor, insira a string de cookies');
                  return;
                }

                // Validar formato básico (mesma validação do backend)
                if (!cookieString.includes('=')) {
                  toast.error('❌ String de cookies inválida: não contém o caractere "=". Verifique se copiou corretamente do navegador.');
                  return;
                }

                if (cookieString.length < 20) {
                  toast.error('❌ String de cookies muito curta (mínimo 20 caracteres). Verifique se copiou todos os cookies.');
                  return;
                }

                // Validar se parece ser uma string de cookies (deve ter pelo menos um ponto e vírgula ou espaço)
                const cookiePattern = /[=;]/;
                if (!cookiePattern.test(cookieString)) {
                  toast.error('❌ Formato de cookies inválido. Certifique-se de copiar a string completa do navegador.');
                  return;
                }

                try {
                  setSaving(true);
                  const url = `${BACKEND_CONFIG.BASE_URL}/api/settings/bitrix-cookies?instance=${instance}`;
                  const requestBody = {
                    cookies: cookieString,
                    expiresIn: 24 * 60 * 60 * 1000 // 24 horas
                  };
                
                  console.log('💾 Salvando cookies:', { url, cookiesLength: cookieString.length, preview: cookieString.substring(0, 50) + '...' });
                  
                  const response = await fetch(url, {
                    method: 'POST',
                    headers: { 
                      'Content-Type': 'application/json',
                      'Accept': 'application/json'
                    },
                    body: JSON.stringify(requestBody)
                  });

                  console.log('📡 Resposta do salvamento:', response.status, response.statusText);

                  let data;
                  try {
                    data = await response.json();
                  } catch (parseError) {
                    console.error('❌ Erro ao parsear resposta:', parseError);
                    const text = await response.text();
                    console.error('📄 Resposta em texto:', text);
                    toast.error(`❌ Erro ao salvar cookies: Resposta inválida do servidor (${response.status})`);
                    return;
                  }
                  
                  console.log('📊 Dados recebidos:', data);
                  
                  if (response.ok && data.success) {
                    // Parsear cookies para exibir separadamente (só os que acabaram de ser salvos)
                    const cookiePairs = cookieString.split(';').map(c => c.trim()).filter(c => c);
                    const cookiesList = cookiePairs.map(cookiePair => {
                      const [name, ...valueParts] = cookiePair.split('=');
                      const value = valueParts.join('=');
                      return {
                        name: name.trim(),
                        value: value.trim(),
                        full: cookiePair.trim()
                      };
                    });

                    // Limpar o campo e mostrar só os novos — não misturar com antigos
                    setCookiesState(prev => ({
                      ...prev,
                      cookieString: '', // Limpar textarea para próxima colagem ser só os novos
                      status: 'active',
                      timestamp: data.data?.timestamp || new Date().toISOString(),
                      expiresAt: data.data?.expiresAt || data.expiresAt,
                      cookiesList: cookiesList
                    }));
                    toast.success(`✅ ${cookiesList.length} cookie(s) do Bitrix ${instanceName} salvos! Os anteriores foram substituídos. Validade: 24h`);
                    await loadBitrixCookiesStatus(instance);
                  } else {
                    const errorMessage = data.message || data.error || 'Erro desconhecido';
                    console.error('❌ Erro na resposta:', errorMessage);
                    toast.error('❌ Erro ao salvar cookies: ' + errorMessage);
                  }
                } catch (error) {
                  console.error('❌ Erro ao salvar cookies:', error);
                  toast.error('❌ Erro ao salvar cookies: ' + (error.message || 'Erro de conexão'));
                } finally {
                  setSaving(false);
                }
              }}
              disabled={saving}
              className={`ml-auto flex items-center gap-2 px-6 py-2 ${isOld ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700' : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'} text-white rounded-lg transition-all disabled:opacity-50`}
            >
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'Salvando...' : `Salvar Cookies ${instanceName}`}
            </button>
          </div>

          {/* Botões de Ação Adicionais */}
          <div className="mt-4 flex gap-3 justify-end">
            <button
              onClick={async () => {
                try {
                  setLoading(true);
                  await loadBitrixCookiesStatus(instance);
                  toast.success(`✅ Status dos cookies do Bitrix ${instanceName} atualizado!`);
                } catch (error) {
                  console.error('Erro ao atualizar status:', error);
                  toast.error(`❌ Erro ao atualizar status dos cookies do Bitrix ${instanceName}`);
                } finally {
                  setLoading(false);
                }
              }}
              disabled={loading}
              className={`flex items-center gap-2 px-4 py-2 ${isOld ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'bg-green-100 text-green-700 hover:bg-green-200'} rounded-lg transition-colors disabled:opacity-50`}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Atualizar Status
            </button>

            <button
              onClick={async () => {
                if (!window.confirm(`Tem certeza que deseja remover os cookies do Bitrix ${instanceName}? Esta ação não pode ser desfeita.`)) {
                  return;
                }

                try {
                  setSaving(true);
                  const response = await fetch(`${BACKEND_CONFIG.BASE_URL}/api/settings/bitrix-cookies?instance=${instance}`, {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' }
                  });

                  const data = await response.json();
                  
                  if (data.success) {
                    setCookiesState({
                      status: 'inactive',
                      timestamp: null,
                      expiresAt: null,
                      cookieString: '',
                      cookiesList: []
                    });
                    toast.success(`✅ Cookies do Bitrix ${instanceName} removidos com sucesso!`);
                    await loadBitrixCookiesStatus(instance);
                  } else {
                    toast.error('❌ Erro ao remover cookies: ' + (data.message || 'Erro desconhecido'));
                  }
                } catch (error) {
                  console.error('Erro ao remover cookies:', error);
                  toast.error('❌ Erro ao remover cookies: ' + error.message);
                } finally {
                  setSaving(false);
                }
              }}
              disabled={saving || cookiesState.status === 'inactive'}
              className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-4 h-4" />
              Remover Cookies
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return renderGeneralTab();
      case 'api':
        return renderApiTab();
      case 'security':
        return renderSecurityTab();
      case 'notifications':
        return renderNotificationsTab();
      case 'bitrix-cookies':
        return renderBitrixCookiesTab();
      default:
        return renderGeneralTab();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Carregando configurações...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="min-h-screen bg-gray-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl">
              <Settings className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Configurações do Sistema</h1>
              <p className="text-gray-600">Gerencie as configurações e preferências do sistema</p>
            </div>
          </div>
          
          {/* Barra de Status */}
          <div className="flex items-center space-x-4 text-sm">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-gray-600">Configurações carregadas</span>
            </div>
            <div className="flex items-center space-x-2">
              <User className="w-4 h-4 text-blue-500" />
              <span className="text-gray-600">Usuário: {user?.name || 'Admin'}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Shield className="w-4 h-4 text-purple-500" />
              <span className="text-gray-600">Role: {user?.role || 'admin'}</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => {
                const IconComponent = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <IconComponent className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
          
          {/* Conteúdo da Tab */}
          <div className="p-6">
            {renderTabContent()}
          </div>
        </div>

        {/* Ações */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={resetSettings}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center space-x-2"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Redefinir</span>
              </button>
              
              <button
                onClick={loadSettings}
                className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors flex items-center space-x-2"
              >
                <FileText className="w-4 h-4" />
                <span>Recarregar</span>
              </button>
            </div>
            
            <button
              onClick={saveSettings}
              disabled={saving}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 flex items-center space-x-3 font-medium shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <Save className="w-5 h-5" />
              )}
              <span>{saving ? 'Salvando...' : 'Salvar Configurações'}</span>
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default SettingsPage;
